import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock, MapPin, Users, ShieldCheck, Hourglass, CreditCard } from "lucide-react";
import { toast } from "sonner";
import CommunityLayout from "@/components/community/CommunityLayout";
import { EventCard, EmptyState, SectionTitle } from "@/components/community/Cards";
import { cApi, eventImg, fmtDate, fmtTime, errMsg, ELIGIBILITY_LABEL, useCommunityMe, PRIVE_SHOP_URL } from "@/lib/community";
import { startGoogleLogin } from "@/context/CustomerAuthContext";

export function EventsList() {
  const [data, setData] = useState({ items: [], categories: [] });
  const [cat, setCat] = useState("");
  const [when, setWhen] = useState("upcoming");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    cApi.get("/community/events", { params: { when, ...(cat ? { category: cat } : {}) } })
      .then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [cat, when]);

  return (
    <CommunityLayout>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-10">
        <SectionTitle eyebrow="Jenova Experiences" title="Gather, grow &" em="glow together" testId="events-title">
          Pilates mornings, girls' brunches, sip & paint nights, photoshoots, faith conversations, travel and more.
        </SectionTitle>
        <div className="mt-10 flex flex-wrap items-center gap-2">
          {["upcoming", "past"].map((w) => (
            <button key={w} className="jc-chip capitalize" data-active={when === w} onClick={() => setWhen(w)} data-testid={`events-when-${w}`}>{w}</button>
          ))}
          <span className="w-px h-6 bg-[#E8DCCB] mx-2" />
          <button className="jc-chip" data-active={!cat} onClick={() => setCat("")} data-testid="events-filter-all">All</button>
          {data.categories.map((c) => (
            <button key={c} className="jc-chip" data-active={cat === c} onClick={() => setCat(c)} data-testid={`events-filter-${c.toLowerCase().replace(/\W+/g, "-")}`}>{c}</button>
          ))}
        </div>
      </section>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24">
        {loading ? null : data.items.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="events-grid">
            {data.items.map((e, i) => <EventCard key={e.id} event={e} idx={i} />)}
          </div>
        ) : (
          <EmptyState title={when === "upcoming" ? "New experiences coming soon" : "No past experiences yet"} testId="events-empty">
            Follow the Weekly Bulletin to hear first when RSVPs open.
          </EmptyState>
        )}
      </section>
    </CommunityLayout>
  );
}

const money = (cents) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

function RsvpStatus({ e, busy, cancel, pay }) {
  const r = e.my_rsvp;
  const paidEvent = e.price > 0;
  const canRefund = r.paid && e.refund_deadline && new Date() <= new Date(e.refund_deadline);
  if (r.status === "pending_payment")
    return (
      <div className="space-y-4">
        <p className="inline-flex items-center gap-2 text-sm" data-testid="event-my-rsvp-status">
          <CreditCard className="w-4 h-4 text-[#A8865A]" strokeWidth={1.25} /> Your spot is held{r.hold_expires_at ? ` until ${fmtTime(r.hold_expires_at)}${new Date(r.hold_expires_at).toDateString() !== new Date().toDateString() ? ` on ${fmtDate(r.hold_expires_at, { month: "short", day: "numeric" })}` : ""}` : ""}.
        </p>
        <button onClick={pay} disabled={busy} className="jc-btn jc-btn-primary w-full" data-testid="event-complete-payment-btn">Complete payment · {money(e.my_price_cents ?? Math.round(e.price * 100))}</button>
        <button onClick={cancel} disabled={busy} className="jc-btn jc-btn-ghost w-full" data-testid="event-cancel-rsvp-btn">Release my spot</button>
      </div>
    );
  return (
    <div className="space-y-4">
      <p className="inline-flex items-center gap-2 text-sm" data-testid="event-my-rsvp-status">
        {r.status === "confirmed" ? <ShieldCheck className="w-4 h-4 text-[#A8865A]" strokeWidth={1.25} /> : <Hourglass className="w-4 h-4 text-[#A8865A]" strokeWidth={1.25} />}
        {r.status === "confirmed" ? `You're confirmed${r.paid ? " — ticket paid" : ""}. See you there!` : `You're #${r.position || "–"} on the waitlist.`}
      </p>
      {paidEvent && r.status === "confirmed" && r.paid && (
        <p className="text-xs text-[#7A6A62]" data-testid="event-refund-note">{canRefund ? `Cancel before ${fmtDate(e.refund_deadline, { month: "long", day: "numeric" })} for a full refund.` : "The refund window has passed for this experience."}</p>
      )}
      <button onClick={() => cancel(canRefund)} disabled={busy} className="jc-btn jc-btn-ghost w-full" data-testid="event-cancel-rsvp-btn">
        {r.status === "confirmed" ? "Cancel my RSVP" : "Leave the waitlist"}
      </button>
    </div>
  );
}

function RsvpBox({ e, onChange }) {
  const { user, level } = useCommunityMe();
  const [busy, setBusy] = useState(false);
  const origin = { origin_url: window.location.origin };

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const rsvp = () => run(async () => {
    const { data } = await cApi.post(`/community/events/${e.id}/rsvp`, origin);
    if (data.checkout_url) return window.location.assign(data.checkout_url);
    toast.success(data.status === "confirmed" ? "You're confirmed! Check your inbox." : `Event is full — you're #${data.position} on the waitlist.`);
    onChange();
  });
  const pay = () => run(async () => {
    const { data } = await cApi.post(`/community/events/${e.id}/pay`, origin);
    window.location.assign(data.checkout_url);
  });
  const cancel = (refundable) => {
    const paid = e.my_rsvp?.paid;
    const msg = paid ? (refundable ? "Cancel your RSVP? Your ticket will be fully refunded." : "Cancel your RSVP? The refund window has passed, so your ticket won't be refunded.") : "Cancel your RSVP?";
    if (!window.confirm(msg)) return;
    run(async () => {
      const { data } = await cApi.delete(`/community/events/${e.id}/rsvp`);
      toast.success(data.refunded ? "RSVP cancelled — your refund is on its way" : "Your RSVP has been cancelled");
      onChange();
    });
  };

  const full = e.spots_left === 0;
  const price = e.price > 0 ? money(e.my_price_cents ?? Math.round(e.price * 100)) : null;
  const isPriveDeal = e.price > 0 && e.prive_discount_percent > 0 && e.my_price_cents < Math.round(e.price * 100);
  let action;
  const priveLocked = e.eligibility === "prive" && !e.can_rsvp_level && e.status !== "cancelled" && !e.my_rsvp;
  if (e.status === "cancelled") action = <p className="jc-display italic text-2xl text-[#A8865A]" data-testid="event-cancelled-note">This experience has been cancelled.</p>;
  else if (e.my_rsvp) action = <RsvpStatus e={e} busy={busy} cancel={cancel} pay={pay} />;
  else if (!e.registration_open) action = <p className="text-sm text-[#7A6A62]" data-testid="event-registration-closed">Registration has closed.</p>;
  else if (priveLocked) action = (
    <div className="space-y-3" data-testid="event-prive-only-notice">
      <p className="font-serif text-xl">This event is for Privé members only.</p>
      <p className="text-sm text-[#4A403B]">To become a Privé member, purchase one Jenova set — a three-piece legging set or a short set — then request Privé verification.</p>
      <Link to={PRIVE_SHOP_URL} className="jc-btn jc-btn-primary w-full" data-testid="event-prive-shop-btn">Shop qualifying sets</Link>
      <Link to="/prive" className="jc-btn jc-btn-ghost w-full" data-testid="event-eligibility-cta">How Privé works</Link>
    </div>
  );
  else if (!user) action = <button onClick={() => startGoogleLogin(`/events/${e.id}`)} className="jc-btn jc-btn-primary w-full" data-testid="event-signin-to-rsvp-btn">Sign in to RSVP</button>;
  else if (!e.can_rsvp_level) action = (
    <div className="space-y-3">
      <p className="text-sm text-[#7A6A62]">This experience is reserved for {ELIGIBILITY_LABEL[e.eligibility]}.</p>
      <Link to={e.eligibility === "prive" ? "/prive" : "/become-a-jenova-girl"} className="jc-btn jc-btn-blush w-full" data-testid="event-eligibility-cta">
        {e.eligibility === "prive" ? "Discover Privé Club" : level === "visitor" ? "Become a Jenova Girl" : "Learn more"}
      </Link>
    </div>
  );
  else action = (
    <button onClick={rsvp} disabled={busy} className="jc-btn jc-btn-primary w-full" data-testid="event-rsvp-btn">
      {busy ? "One moment…" : full ? "Join the waitlist" : price ? `Reserve & pay · ${price}` : "RSVP — reserve my spot"}
    </button>
  );

  return (
    <aside className="bg-[#F5EFE6] p-8 md:p-10 rounded-sm md:sticky md:top-40" data-testid="event-rsvp-box">
      <p className="jc-eyebrow" data-testid="event-ticket-price">{price ? `Ticket · ${price}` : "Reserve your spot · Free"}</p>
      {isPriveDeal && (
        <p className="mt-2 text-xs text-[#A8865A]" data-testid="event-prive-discount-applied">
          <span className="line-through text-[#7A6A62] mr-1">{money(Math.round(e.price * 100))}</span> Privé member price — {e.prive_discount_percent}% off
        </p>
      )}
      {!isPriveDeal && e.price > 0 && e.prive_discount_percent > 0 && (
        <p className="mt-2 text-xs text-[#7A6A62]" data-testid="event-prive-discount-teaser">
          Privé members pay {money(e.prive_price_cents)} ({e.prive_discount_percent}% off). <Link to="/prive" className="underline">Learn more</Link>
        </p>
      )}
      <p className="jc-display text-5xl mt-4" data-testid="event-spots-left">{e.spots_left}<span className="text-xl text-[#7A6A62]"> / {e.capacity} spots left</span></p>
      <div className="mt-6 space-y-2 text-sm text-[#4A403B]">
        <p>Eligibility: <strong className="font-normal">{ELIGIBILITY_LABEL[e.eligibility]}</strong>{e.eligibility_note ? ` · ${e.eligibility_note}` : ""}</p>
        {e.registration_deadline && <p>RSVP by {fmtDate(e.registration_deadline, { month: "long", day: "numeric" })} at {fmtTime(e.registration_deadline)}</p>}
        {price && e.refund_deadline && <p data-testid="event-refund-policy">Full refund if you cancel before {fmtDate(e.refund_deadline, { month: "long", day: "numeric" })}.</p>}
        {price && full && !e.my_rsvp && <p className="text-xs text-[#7A6A62]">Joining the waitlist is free — if a spot opens we'll hold it for you for 24 hours to pay.</p>}
      </div>
      <div className="mt-8">{action}</div>
    </aside>
  );
}

export function EventDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [e, setE] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    cApi.get(`/community/events/${id}`).then(({ data }) => setE(data)).catch((er) => setError(errMsg(er, "Event not found")));
  }, [id]);
  const handled = useRef(null);
  useEffect(() => {
    if (handled.current === id) return;
    handled.current = id;
    const sid = params.get("ticket_session");
    if (params.get("ticket_cancelled")) {
      toast("Payment not completed — your spot is held for a short time.");
      setParams({}, { replace: true });
    }
    if (!sid) return load();
    cApi.post(`/community/events/${id}/confirm-payment`, { session_id: sid })
      .then(({ data }) => {
        if (data.result === "confirmed") toast.success("Payment received — you're confirmed! Check your inbox.");
        else if (data.result === "refunded") toast.error("Your spot was released before payment completed, so you've been fully refunded.");
        else toast("We're confirming your payment — this can take a moment.");
      })
      .catch((er) => toast.error(errMsg(er)))
      .finally(() => {
        setParams({}, { replace: true });
        load();
      });
    // eslint-disable-next-line
  }, [id]);

  return (
    <CommunityLayout>
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pt-12 pb-24" data-testid="event-detail">
        <Link to="/events" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#7A6A62] hover:text-[#2B2522]" data-testid="event-back-link">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.25} /> All experiences
        </Link>
        {error && <div className="mt-10"><EmptyState title={error} /></div>}
        {e && (
          <div className="mt-10 grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <div className="jc-card-img aspect-[4/3]"><img src={eventImg(e)} alt={e.title} className="w-full h-full object-cover" /></div>
              <p className="jc-eyebrow mt-12">{e.category}</p>
              <h1 className="jc-display text-4xl sm:text-5xl lg:text-6xl mt-4" data-testid="event-detail-title">{e.title}</h1>
              <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm text-[#4A403B]">
                <p className="inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#A8865A]" strokeWidth={1.25} />{fmtDate(e.start_at, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
                <p className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#A8865A]" strokeWidth={1.25} />{fmtTime(e.start_at)}{e.end_at ? ` – ${fmtTime(e.end_at)}` : ""}</p>
                <p className="inline-flex items-center gap-2" data-testid="event-location"><MapPin className="w-4 h-4 text-[#A8865A]" strokeWidth={1.25} />{e.location}</p>
                <p className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-[#A8865A]" strokeWidth={1.25} />{e.capacity} guests</p>
              </div>
              <div className="mt-10 text-[15px] md:text-base leading-[1.9] text-[#4A403B] whitespace-pre-line">{e.description}</div>
            </div>
            <div className="md:col-span-4 md:col-start-9"><RsvpBox e={e} onChange={load} /></div>
          </div>
        )}
      </div>
    </CommunityLayout>
  );
}
